// Corridor-Selfservice API-Client („Corridor Downloads")
// ======================================================
// Frontend-Gegenstück zu backend/routes/corridors.js (Abschnitt 2 der
// docs/corridor-selfservice-spec.md): GPX hochladen → Server baut den
// kompletten Offline-Korridor (Karte, Contours, Hillshade, Routing, POIs)
// → Paket erscheint hier zum Download.
//
// Auth: authFetch (Bearer-Token-Fallback); im Browser trägt zusätzlich das
// HttpOnly-Cookie. Die Datei-Downloads (<a href>) laufen rein übers Cookie —
// deshalb funktionieren sie nur eingeloggt im selben Browser-Kontext.

import { authFetch } from './auth';

// 'expired' = das Offline-Paket ist nach 72 h weggeräumt, die Zeile lebt
// aber weiter, weil die Navigationsdaten noch 14 Tage gültig sind.
export type CorridorJobStatus = 'queued' | 'running' | 'done' | 'error' | 'expired';

export type CorridorNaviStatus = 'none' | 'pending' | 'active' | 'expired';

export interface CorridorNaviInfo {
  status: CorridorNaviStatus;
  /** Ende der 14-Tage-Frist; danach löscht der Server die Routing-Graphen. */
  expiresAt: string | null;
  retentionDays: number;
  /** In wie viele Bereiche der Korridor fürs Routing zerlegt ist. */
  areas: number;
}

export interface CorridorJobProgress {
  step: number;
  total: number;
  label: string | null;
  pct: number;
}

export interface CorridorJob {
  id: string;
  name: string;
  regionSlug: string;
  widthKm: number;
  status: CorridorJobStatus;
  progress: CorridorJobProgress;
  queuePosition: number;
  error?: string | null;
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  /** 72-h-Retention: ab diesem Zeitpunkt räumt der Server das Paket weg. */
  expiresAt?: string | null;
  /** Gesetzt, sobald das Paket weggeräumt ist (Zeile lebt nur noch fürs Navi). */
  packageRemovedAt?: string | null;
  navi?: CorridorNaviInfo;
  artifactCount: number;
}

/**
 * Pro User darf genau EIN Korridor Navigationsdaten haben (die Routing-
 * Graphen belegen dauerhaft Server-RAM und Platz). `heldBy` sagt, wer den
 * Slot gerade hat — null heisst frei.
 */
export interface CorridorNaviSlot {
  retentionDays: number;
  heldBy: {
    id: string;
    name: string;
    regionSlug: string;
    status: CorridorNaviStatus;
    expiresAt: string | null;
    areas: number;
  } | null;
}

/** "noch 63 h" / "noch 45 min" / "läuft gleich ab" für die Retention-Anzeige. */
export function expiresInText(expiresAt: string | null | undefined): string | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return 'läuft gleich ab';
  const h = ms / 3600000;
  if (h >= 2) return `noch ${Math.round(h)} h verfügbar`;
  const min = Math.max(1, Math.round(ms / 60000));
  return `noch ${min} min verfügbar`;
}

/** "noch 12 Tage" / "noch 6 h" — Countdown der 14-Tage-Navigationsfrist. */
export function naviExpiresInText(expiresAt: string | null | undefined): string | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return 'läuft gerade ab';
  const d = ms / 86400000;
  if (d >= 2) return `noch ${Math.floor(d)} Tage`;
  const h = ms / 3600000;
  if (h >= 1) return `noch ${Math.round(h)} h`;
  return `noch ${Math.max(1, Math.round(ms / 60000))} min`;
}

export interface CorridorArtifact {
  file: string;
  sizeBytes: number;
  url: string;
}

export interface CorridorManifest {
  job: CorridorJob;
  regionSlug: string;
  meta: {
    bbox?: { west: number; south: number; east: number; north: number };
    center?: [number, number];
    lengthKm?: number;
    srtmTiles?: string[];
  } | null;
  artifacts: CorridorArtifact[];
  totalBytes: number;
}

/** Verständliche Meldungen für die bekannten Server-Fehlercodes. */
export function corridorErrorText(code: string | undefined, extra?: Record<string, unknown>): string {
  switch (code) {
    case 'quota_exceeded':
      return `Limit erreicht (max. ${extra?.max ?? 3} Pakete). Erst ein bestehendes Paket löschen.`;
    case 'gpx_too_large':
      return 'GPX ist zu groß (max. ~20 MB).';
    case 'gpx_no_trackpoints':
      return 'Keine Trackpunkte gefunden — ist das eine gültige GPX mit <trkpt>?';
    case 'gpx_too_many_points':
      return 'Zu viele Trackpunkte (max. 250.000).';
    case 'width_invalid':
      return 'Ungültige Korridor-Breite (10, 20 oder 30 km).';
    case 'name_invalid':
      return 'Name bitte mit 2–60 Zeichen.';
    case 'gpx_required':
      return 'Bitte zuerst eine GPX-Datei wählen.';
    case 'job_running':
      return 'Laufende Builds können nicht gelöscht werden.';
    case 'navi_slot_taken':
      return typeof extra?.hint === 'string'
        ? extra.hint
        : 'Navigationsdaten hat bereits ein anderer Korridor. Es kann immer nur einer navigieren — dort zuerst freigeben.';
    case 'no_navi_data':
      return 'Dieser Korridor hat keine Navigationsdaten.';
    case 'unauthenticated':
      return 'Nicht eingeloggt — bitte neu anmelden.';
    default:
      return code ? `Fehler: ${code}` : 'Unbekannter Fehler.';
  }
}

async function toJsonOrThrow(res: Response): Promise<any> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* leere/kaputte Antwort */
  }
  if (!res.ok) {
    const err = new Error(corridorErrorText(body?.error, body)) as Error & { code?: string };
    err.code = body?.error;
    throw err;
  }
  return body;
}

export async function listCorridors(): Promise<{
  jobs: CorridorJob[];
  workerEnabled: boolean;
  naviSlot?: CorridorNaviSlot;
}> {
  const res = await authFetch('/api/corridors');
  return toJsonOrThrow(res);
}

export async function uploadCorridor(params: {
  gpxXml: string;
  name: string;
  widthKm: number;
  /** false = Korridor ohne Navigation bauen (spart Build-Zeit, Slot bleibt frei). */
  navi?: boolean;
  /** Job-Id, deren Navigationsdaten für diesen Build freigegeben werden sollen. */
  releaseNaviFrom?: string;
}): Promise<{ job: CorridorJob; deduped?: boolean }> {
  const res = await authFetch('/api/corridors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  return toJsonOrThrow(res);
}

export async function retryCorridor(id: string): Promise<{ job: CorridorJob }> {
  const res = await authFetch(`/api/corridors/${encodeURIComponent(id)}/retry`, { method: 'POST' });
  return toJsonOrThrow(res);
}

export async function deleteCorridor(id: string): Promise<void> {
  const res = await authFetch(`/api/corridors/${encodeURIComponent(id)}`, { method: 'DELETE' });
  await toJsonOrThrow(res);
}

/**
 * Gibt die Navigationsdaten eines Korridors frei (löscht Routing-Graphen).
 * Danach ist der Navi-Slot des Users wieder frei. `jobRemoved` heisst: das
 * Paket war schon abgelaufen, die Zeile ist damit ganz verschwunden.
 */
export async function deleteCorridorNavi(id: string): Promise<{ jobRemoved?: boolean }> {
  const res = await authFetch(`/api/corridors/${encodeURIComponent(id)}/navi`, {
    method: 'DELETE',
  });
  return toJsonOrThrow(res);
}

export async function getCorridorManifest(id: string): Promise<CorridorManifest> {
  const res = await authFetch(`/api/corridors/${encodeURIComponent(id)}/manifest`);
  return toJsonOrThrow(res);
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  if (mb >= 10) return `${Math.round(mb)} MB`;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
